// Angular imports
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

// 3rd party imports
import { DxBoxComponent, DxPopupComponent } from 'devextreme-angular';
import { plainToClass } from 'class-transformer';

// CC-Common imports
import { DxWidgetUtils, IconConstants, Utils } from 'cc-common';

// Component & Interface imports
import { IInteractiveDashboardComponent } from '../../../dashboard-module/_interfaces/interactive-dashboard-component';
import { ICommunityContextDashboardComponent } from '../../../dashboard-module/_interfaces/community-context-component';
import { MultiColumnSelectorComponent } from '../../../management-common/component/common/multi-column-selector/multi-column-selector.component';
import { WorkflowQueueDataGridComponent } from '../workflow-queue-datagrid/workflow-queue-datagrid.component';
import { LiteModePreferenceComponent } from '../../../shared-module/lite-mode-preference/lite-mode-preference.component';

// Service imports
import { ScreenService } from '../../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { WorkflowDashboardService } from '../../_services/workflow-dashboard.service';
import { WorkflowEditorService } from '../../../workflow/_services/workflow-editor.service';
import { WorkflowQueueService } from '../../_services/workflow-queue.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';
import { CommunityService } from '../../../_services/data-services/community.service';

// Model imports
import { WorkflowDashboardParametersDto } from '../../_models/workflow-queue/workflow-dashboard-parameters-dto';
import { WorkflowDashboardSummaryResultDto } from '../../_models/workflow-queue/workflow-dashboard-summary-result-dto';
import { WorkflowQueueResultDto } from '../../_models/workflow-queue/workflow-queue-result-dto';
import { WorkFlowQueueParametersDto } from '../../_models/workflow-queue/workflow-queue-parameters-dto';
import { PropertyCommunityInfoResultDto } from '../../../_models/property-community-info-result-dto';
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';

interface IViewOption {
    text: string;
    value: boolean;
}

@Component({
    selector: 'app-workflow-dashboard',
    templateUrl: './workflow-dashboard.component.html',
    styleUrls: ['./workflow-dashboard.component.scss']
})
export class WorkflowDashboardComponent
    implements OnInit, OnDestroy, ICommunityContextDashboardComponent, IInteractiveDashboardComponent
{
    @ViewChild('barChartBox', { static: false }) barChartBox: DxBoxComponent;
    @ViewChild('workflowGrid', { static: false }) workflowGrid: WorkflowQueueDataGridComponent;
    @ViewChild('communitySelector', { static: false }) communitySelector: MultiColumnSelectorComponent;
    @ViewChild('workflowQueueGridPopup', { static: false }) workflowQueueGridPopup: DxPopupComponent;
    @ViewChild('liteModePreference', { static: false }) liteModePreference: LiteModePreferenceComponent;

    iconRefresh = IconConstants.REFRESH;

    hideTitle = true;
    minWidthRatio = 30;
    minHeightRatio = 30;
    minWidth = 500;
    minHeight = 480;
    hideFullScreen = false;
    widgetId: string;

    isSmallScreen: boolean;
    loadingVisible: boolean;
    detailsVisible: boolean;
    detailsLoadingVisible: boolean;
    chartSizeCalculated: boolean;

    popupTitle: string;
    chartWidth = 200;
    chartHeight = 100;

    viewOptions: IViewOption[];

    summaryResults: WorkflowDashboardSummaryResultDto[];
    selectedSummaryResult: WorkflowDashboardSummaryResultDto;
    selectedSummaryResultItems: WorkflowQueueResultDto[];
    selectedContext: { communityId?: number; managementCompanyId?: number } = {};

    dashboardReloadSubscription: Subscription;
    contextChangeSubscription: Subscription;

    communityList: CommunitySelectorResultDto[] = [];
    communityColumns: any[];
    communityGridBoxValue: number[];
    isCommunitySelectorHidden = false;

    isLiteMode: boolean;

    filterRowColCount = 4;
    filterRowColDivider = 140;

    private _selectedViewOption: IViewOption;

    constructor(
        private readonly screenService: ScreenService,
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly workflowDashboardService: WorkflowDashboardService,
        private readonly workflowQueueService: WorkflowQueueService,
        private readonly workflowEditorService: WorkflowEditorService,
        private readonly contextService: ContextManagerService,
        private readonly communityService: CommunityService
    ) {
        this.communityColumns = this.communityService.getCommunitySelectorColumns();
    }

    ngOnInit(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        this.screenService.changed.subscribe(() => {
            this.isSmallScreen = this.screenService.isSmallScreen();
        });

        this.viewOptions = [
            { text: 'Show All Work', value: true },
            { text: 'Show My Work', value: false }
        ];

        this._selectedViewOption = this.viewOptions[0];

        this.dashboardReloadSubscription = this.workflowEditorService
            .getSubjectRefreshWorkflow()
            .subscribe((result: boolean) => {
                this.refreshDashboard();
            });

        this.updateSelectedContext();
        this.contextChangeSubscription = this.contextService
            .getContext()
            .subscribe(this.updateSelectedContext.bind(this));

        if (Utils.isFalse(this.isCommunitySelectorHidden)) {
            this.getCommunityList();
        }

        this.populateDashboardData();
    }

    ngOnDestroy(): void {
        this.dashboardReloadSubscription.unsubscribe();
        this.contextChangeSubscription.unsubscribe();
    }

    public get selectedViewOption(): IViewOption {
        return this._selectedViewOption;
    }
    public set selectedViewOption(v: IViewOption) {
        if (Utils.notEqualsIgnoreCase(this._selectedViewOption.value, v)) {
            this._selectedViewOption = v;

            this.populateDashboardData();
        }
    }

    refresh(width: number, height: number): void {
        this.filterRowColCount = Math.round(width / this.filterRowColDivider);

        if (!!this.barChartBox && !!this.barChartBox.instance) {
            this.onChartContainerReady();
        }
        if (!!this.workflowGrid && !!this.workflowGrid.gridFeatures) {
            this.workflowGrid.gridFeatures.resized({ width, height });
        }
    }

    getTitle(name: string): string {
        return 'AAP Workflow';
    }

    setCommunity(communityId: number, managementCompanyId: number, communityName: string): void {
        if (!this.isCommunitySelectorHidden) {
            if (!!communityId) {
                this.communityGridBoxValue = [communityId];
            } else {
                this.communityGridBoxValue = [];
                if (!!this.communitySelector) {
                    this.communitySelector.resetSelector();
                }
            }
        }
        if (this.selectedContext.communityId !== communityId) {
            this.selectedContext.communityId = communityId;
            this.selectedContext.managementCompanyId = managementCompanyId;
            setTimeout(this.populateDashboardData.bind(this), 500);
        }
    }

    onChartContainerReady(): void {
        setTimeout(() => {
            if (DxWidgetUtils.hasInstance(this.barChartBox)) {
                const el = this.barChartBox.instance.element();
                this.chartWidth = el.clientWidth - 10;
                this.chartHeight = el.clientHeight - 10;
            }
        }, 250);
    }

    repaint(event): void {
        event.component.repaint();
        setTimeout(() => {
            if (!!this.workflowGrid && !!this.workflowGrid.queueGrid && !!this.workflowGrid.queueGrid.instance) {
                this.workflowGrid.queueGrid.instance.repaint();
            }
        }, 250);
    }

    cleanup(): void {
        this.detailsVisible = false;
        this.selectedSummaryResult = null;
        this.selectedSummaryResultItems = null;
    }

    refreshDashboard(): void {
        this.populateDashboardData(true);
    }

    chartPointClick(event): void {
        const selectedItem: WorkflowDashboardSummaryResultDto = plainToClass(
            WorkflowDashboardSummaryResultDto,
            event.target.data
        );
        if (!!selectedItem && selectedItem.status !== 'No Data') {
            this.displayWorkflowData(selectedItem);
        }
    }

    onAxisLabelClick(event: any): void {
        const selectedItem: WorkflowDashboardSummaryResultDto = this.summaryResults.find(
            (r) => r.status === event.argument
        );
        if (!!selectedItem && selectedItem.status !== 'No Data') {
            this.displayWorkflowData(selectedItem);
        }
    }

    chartLegendClick(event): void {
        const series = event.target;
        if (series.isVisible()) {
            series.hide();
        } else {
            series.show();
        }
    }

    onCommunitySelection(selectedObject: any): void {
        if (!this.isCommunitySelectorHidden) {
            if (!!selectedObject && selectedObject.selectedRow.length > 0) {
                const selectedRow: CommunitySelectorResultDto = selectedObject.selectedRow[0];
                if (this.selectedContext.communityId !== selectedRow.communityId) {
                    this.communityGridBoxValue = [selectedRow.communityId];
                    this.selectedContext.communityId = selectedRow.communityId;
                    this.selectedContext.managementCompanyId = selectedRow.managementCompanyId;
                    setTimeout(this.populateDashboardData.bind(this), 500);
                }
            } else if (!!this.selectedContext.communityId) {
                this.selectedContext = {};
                this.communityGridBoxValue = [];
                this.communitySelector.resetSelector();
                setTimeout(this.populateDashboardData.bind(this), 500);
            }
        }
    }

    onForceRefresh(event: boolean): void {
        this.detailsLoadingVisible = true;
        if (Utils.isTrue(event)) {
            this.refreshDashboard();
            this.displayWorkflowData(this.selectedSummaryResult, true);
        }
    }

    isLiteModeDefined(): boolean {
        if (this.isLiteMode === true || this.isLiteMode === false) {
            return true;
        } else {
            return false;
        }
    }

    onLiteModeValueChange(): void {
        this.liteModePreference.show(this.isLiteMode);
        if (!!this.selectedSummaryResult) {
            this.displayWorkflowData(this.selectedSummaryResult);
        }
    }

    setStoredLiteMode(flag: boolean): void {
        setTimeout(() => {
            this.isLiteMode = flag;
            if (!!this.selectedSummaryResult) {
                this.displayWorkflowData(this.selectedSummaryResult, true);
            }
        }, 200);
    }

    private getCommunityList() {
        this.loadingVisible = true;
        this.communityService.getCommunities().subscribe(
            (data) => {
                this.communityList = data;
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private displayWorkflowData(selectedItem: WorkflowDashboardSummaryResultDto, clearCache = false): void {
        const params = new WorkFlowQueueParametersDto();
        params.includeAll = this.selectedViewOption.value;
        params.status = selectedItem.status;
        params.communityId = this.selectedContext.communityId;
        params.liteMode = this.isLiteMode;
        params.clearCache = clearCache;

        if (this.isLiteModeDefined()) {
            this.loadingVisible = true;
            this.workflowQueueService.getWorkflowQueue(params).subscribe(
                (results) => {
                    this.selectedSummaryResult = this.summaryResults.find((d) => d.status === selectedItem.status);
                    this.selectedSummaryResultItems = results;
                    this.popupTitle = `AAP Workflow Queue - ${this.selectedSummaryResult.statusText}`;
                    this.detailsVisible = true;
                    this.loadingVisible = false;
                    this.detailsLoadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                    this.detailsLoadingVisible = false;
                }
            );
        }
    }

    private populateDashboardData(clearCache = false): void {
        this.loadingVisible = true;

        const params = new WorkflowDashboardParametersDto();
        params.communityId = this.selectedContext.communityId;
        params.includeAll = this.selectedViewOption.value;
        params.clearCache = clearCache;

        this.workflowDashboardService.getDashboardSummary(params).subscribe(
            (results: WorkflowDashboardSummaryResultDto[]) => {
                if (results.length === 0) {
                    const empty = new WorkflowDashboardSummaryResultDto();
                    empty.status = 'No Data';
                    empty.itemCount = 0;
                    empty.sequence = 0;
                    results.push(empty);
                }
                this.loadingVisible = false;
                this.summaryResults = this.helperService.sortSequenceItems(results);
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private updateSelectedContext() {
        const context = this.contextService.getSelectedContext();

        if (Utils.notNullAndDefined(context)) {
            this.isCommunitySelectorHidden = true;
            this.communityGridBoxValue = [context.communityId];
            this.selectedContext.communityId = context.communityId;
            this.selectedContext.managementCompanyId = context.managementCompanyId;
        }
    }
}
